import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

interface VisualStep {
  id: number;
  name: string;
  status: 'completed' | 'active' | 'pending' | 'failed';
  showSpinner: boolean;
}

interface TransferProgress {
  transferId: string;
  status: string;
  currentStep: number;
  totalSteps: number;
  stepName: string;
  stepDescription: string;
  visualSteps: VisualStep[];
  timestamp: string;
  withdrawnAmount?: string;
  estimatedUnlockSeconds?: number;
  currentConfirms?: number;
  requiredConfirms?: number;
  estimatedCompletionSeconds?: number;
  totalCurrentConfirms?: number;
  totalRequiredConfirms?: number;
}

interface WebSocketContextType {
  isConnected: boolean;
  subscribeToTransfer: (transferId: string) => void;
  unsubscribeFromTransfer: (transferId: string) => void;
  onProgress: (callback: (progress: TransferProgress) => void) => () => void;
  onCompleted: (callback: (data: any) => void) => () => void;
  onFailed: (callback: (data: any) => void) => () => void;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

const WS_URL = (() => {
  const explicit = process.env.REACT_APP_WS_URL;
  if (explicit) return explicit;
  const apiBase = process.env.REACT_APP_API_BASE_URL || 'https://api.obscurium.pro';
  try {
    const url = new URL(apiBase);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.pathname = '/ws';
    return url.toString();
  } catch {
    return 'wss://api.obscurium.pro/ws';
  }
})();

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const progressCallbacksRef = useRef<Set<(progress: TransferProgress) => void>>(new Set());
  const completedCallbacksRef = useRef<Set<(data: any) => void>>(new Set());
  const failedCallbacksRef = useRef<Set<(data: any) => void>>(new Set());
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const subscribedTransfersRef = useRef<Set<string>>(new Set());

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);

        // Re-subscribe to any transfers we were subscribed to
        subscribedTransfersRef.current.forEach(transferId => {
          ws.send(JSON.stringify({
            type: 'subscribe:transfer',
            transferId
          }));
        });
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);

        // Attempt to reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 3000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          switch (data.type) {
          case 'transfer:progress':
            const merged = {
              ...data.data,
              withdrawnAmount: data.withdrawnAmount,
              estimatedUnlockSeconds: data.estimatedUnlockSeconds,
              currentConfirms: data.currentConfirms,
              requiredConfirms: data.requiredConfirms,
              estimatedCompletionSeconds: data.estimatedCompletionSeconds,
              totalCurrentConfirms: data.totalCurrentConfirms,
              totalRequiredConfirms: data.totalRequiredConfirms
            };
            progressCallbacksRef.current.forEach(callback => {
              callback(merged);
            });
            break;
            case 'transfer:completed':
              completedCallbacksRef.current.forEach(callback => {
                callback(data);
              });
              break;
            case 'transfer:failed':
              failedCallbacksRef.current.forEach(callback => {
                callback(data);
              });
              break;
            case 'subscribed':
              console.log('Subscribed to transfer:', data.transferId);
              break;
            case 'unsubscribed':
              console.log('Unsubscribed from transfer:', data.transferId);
              break;
            case 'pong':
              // Heartbeat response
              break;
            default:
              console.log('Unknown WebSocket message type:', data.type);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
    }
  }, []);

  useEffect(() => {
    connect();

    // Heartbeat to keep connection alive
    const heartbeatInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      clearInterval(heartbeatInterval);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  const subscribeToTransfer = useCallback((transferId: string) => {
    subscribedTransfersRef.current.add(transferId);

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'subscribe:transfer',
        transferId
      }));
    }
  }, []);

  const unsubscribeFromTransfer = useCallback((transferId: string) => {
    subscribedTransfersRef.current.delete(transferId);

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'unsubscribe:transfer',
        transferId
      }));
    }
  }, []);

  const onProgress = useCallback((callback: (progress: TransferProgress) => void) => {
    progressCallbacksRef.current.add(callback);
    return () => {
      progressCallbacksRef.current.delete(callback);
    };
  }, []);

  const onCompleted = useCallback((callback: (data: any) => void) => {
    completedCallbacksRef.current.add(callback);
    return () => {
      completedCallbacksRef.current.delete(callback);
    };
  }, []);

  const onFailed = useCallback((callback: (data: any) => void) => {
    failedCallbacksRef.current.add(callback);
    return () => {
      failedCallbacksRef.current.delete(callback);
    };
  }, []);

  return (
    <WebSocketContext.Provider
      value={{
        isConnected,
        subscribeToTransfer,
        unsubscribeFromTransfer,
        onProgress,
        onCompleted,
        onFailed
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
